import React, { useEffect, useState } from 'react';
import api from '../../lib/axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { CalendarClock, Plus, Pencil, Trash2, X, Loader2, Save, Send } from 'lucide-react';
import { toast } from 'sonner';
import { listaCompletaPapeis } from '../../lib/papeisNotificacao';

const DIA_LABEL = {
  segunda: 'Seg', terca: 'Ter', quarta: 'Qua', quinta: 'Qui',
  sexta: 'Sex', sabado: 'Sáb', domingo: 'Dom',
};

const AGENDAMENTO_VAZIO = {
  nome_alerta: '',
  tipo_relatorio: '',
  frequencia: [],
  horarios: [],
  usuarios_manuais: [],
  papeis_automaticos: [],
  ativo: true,
};

export const AgendamentosRelatorios = () => {
  const [agendamentos, setAgendamentos] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [diasSemana, setDiasSemana] = useState(Object.keys(DIA_LABEL));
  const [usuarios, setUsuarios] = useState([]);
  const [papeisCatalogo, setPapeisCatalogo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState(AGENDAMENTO_VAZIO);
  const [novoHorario, setNovoHorario] = useState('09:00');
  const [saving, setSaving] = useState(false);
  const [excluindoId, setExcluindoId] = useState(null);
  const [testandoId, setTestandoId] = useState(null);

  useEffect(() => {
    carregarTudo();
  }, []);

  const carregarTudo = async () => {
    try {
      setLoading(true);
      const [ags, catalogo, usrs, papeis] = await Promise.all([
        api.get('/alertas-agendados'),
        api.get('/alertas-agendados/tipos-relatorio'),
        api.get('/users/simple'),
        api.get('/users/papeis-disponiveis'),
      ]);
      setAgendamentos(ags.data || []);
      setTipos(catalogo.data?.tipos || []);
      setDiasSemana(catalogo.data?.dias_semana || Object.keys(DIA_LABEL));
      setUsuarios(usrs.data || []);
      setPapeisCatalogo(listaCompletaPapeis(papeis.data || []));
    } catch (e) {
      toast.error('Erro ao carregar agendamentos');
    } finally {
      setLoading(false);
    }
  };

  const abrirNovo = () => {
    setEditandoId(null);
    setForm({ ...AGENDAMENTO_VAZIO, tipo_relatorio: tipos[0]?.valor || '' });
    setNovoHorario('09:00');
    setDialogOpen(true);
  };

  const abrirEditar = (ag) => {
    setEditandoId(ag.id);
    setForm({
      nome_alerta: ag.nome_alerta || '',
      tipo_relatorio: ag.tipo_relatorio || '',
      frequencia: ag.frequencia || [],
      horarios: ag.horarios || [],
      usuarios_manuais: ag.usuarios_manuais || [],
      papeis_automaticos: ag.papeis_automaticos || [],
      ativo: ag.ativo ?? true,
    });
    setNovoHorario('09:00');
    setDialogOpen(true);
  };

  const toggleDia = (dia) => {
    setForm((f) => ({
      ...f,
      frequencia: f.frequencia.includes(dia)
        ? f.frequencia.filter((d) => d !== dia)
        : [...f.frequencia, dia],
    }));
  };

  const adicionarHorario = () => {
    if (!novoHorario) return;
    if (form.horarios.includes(novoHorario)) {
      toast.error('Horário já adicionado');
      return;
    }
    setForm((f) => ({ ...f, horarios: [...f.horarios, novoHorario].sort() }));
  };

  const removerHorario = (h) => {
    setForm((f) => ({ ...f, horarios: f.horarios.filter((x) => x !== h) }));
  };

  const toggleDestinatario = (userId) => {
    setForm((f) => ({
      ...f,
      usuarios_manuais: f.usuarios_manuais.includes(userId)
        ? f.usuarios_manuais.filter((id) => id !== userId)
        : [...f.usuarios_manuais, userId],
    }));
  };

  const togglePapel = (valor) => {
    setForm((f) => ({
      ...f,
      papeis_automaticos: f.papeis_automaticos.includes(valor)
        ? f.papeis_automaticos.filter((x) => x !== valor)
        : [...f.papeis_automaticos, valor],
    }));
  };

  const handleSalvar = async () => {
    if (!form.nome_alerta.trim()) {
      toast.error('Informe o nome do alerta');
      return;
    }
    if (!form.tipo_relatorio) {
      toast.error('Selecione o tipo de relatório');
      return;
    }
    if (form.frequencia.length === 0) {
      toast.error('Selecione ao menos um dia da semana');
      return;
    }
    if (form.horarios.length === 0) {
      toast.error('Adicione ao menos um horário');
      return;
    }
    if (form.usuarios_manuais.length === 0 && form.papeis_automaticos.length === 0) {
      toast.error('Selecione ao menos um destinatário (usuário específico ou papel automático)');
      return;
    }
    try {
      setSaving(true);
      if (editandoId) {
        await api.put(`/alertas-agendados/${editandoId}`, form);
        toast.success('Agendamento atualizado');
      } else {
        await api.post('/alertas-agendados', form);
        toast.success('Agendamento criado');
      }
      setDialogOpen(false);
      await carregarTudo();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar agendamento');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAtivo = async (ag) => {
    try {
      await api.put(`/alertas-agendados/${ag.id}`, { ativo: !ag.ativo });
      setAgendamentos((prev) => prev.map((a) => (a.id === ag.id ? { ...a, ativo: !a.ativo } : a)));
    } catch (e) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleExcluir = async () => {
    if (!excluindoId) return;
    try {
      await api.delete(`/alertas-agendados/${excluindoId}`);
      toast.success('Agendamento excluído');
      setAgendamentos((prev) => prev.filter((a) => a.id !== excluindoId));
    } catch (e) {
      toast.error('Erro ao excluir agendamento');
    } finally {
      setExcluindoId(null);
    }
  };

  const handleTestarAgora = async (ag) => {
    try {
      setTestandoId(ag.id);
      const { data } = await api.post(`/alertas-agendados/${ag.id}/disparar-agora`);
      if (data.destinatarios > 0) {
        toast.success(`Disparado agora para ${data.destinatarios} destinatário(s)`);
      } else {
        toast.warning('Disparado, mas nenhum destinatário foi resolvido (verifique usuários/papéis)');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao disparar agendamento');
    } finally {
      setTestandoId(null);
    }
  };

  const labelTipo = (valor) => tipos.find((t) => t.valor === valor)?.label || valor;
  const nomeUsuario = (id) => usuarios.find((u) => u.id === id)?.full_name || usuarios.find((u) => u.id === id)?.email || id;
  const labelPapel = (valor) => papeisCatalogo.find((p) => p.valor === valor)?.label || valor;

  return (
    <Card data-testid="agendamentos-relatorios-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-amber-600" />
              Agendamentos de Relatórios
            </CardTitle>
            <CardDescription>
              Configure relatórios recorrentes (ex: pendências de Compras) enviados automaticamente por WhatsApp e in-app,
              em dias e horários específicos (fuso de Brasília). Use "Testar agora" para disparar imediatamente e validar o conteúdo.
            </CardDescription>
          </div>
          <Button data-testid="novo-agendamento-btn" onClick={abrirNovo} size="sm">
            <Plus className="w-4 h-4 mr-1" /> Novo agendamento
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : agendamentos.length === 0 ? (
          <p data-testid="agendamentos-vazio" className="text-sm text-slate-500 py-4 text-center">
            Nenhum agendamento configurado ainda.
          </p>
        ) : (
          <div className="space-y-3" data-testid="agendamentos-lista">
            {agendamentos.map((ag) => (
              <div
                key={ag.id}
                data-testid={`agendamento-item-${ag.id}`}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded border bg-slate-50"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{ag.nome_alerta}</span>
                    <Badge variant="outline" className="text-xs">{labelTipo(ag.tipo_relatorio)}</Badge>
                    {!ag.ativo && <Badge className="text-xs bg-slate-200 text-slate-600">Pausado</Badge>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                    {(ag.frequencia || []).map((d) => (
                      <Badge key={d} variant="secondary" className="text-[10px] px-1.5 py-0">{DIA_LABEL[d] || d}</Badge>
                    ))}
                    {(ag.horarios || []).map((h) => (
                      <Badge key={h} className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-800">{h}</Badge>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {(ag.usuarios_manuais || []).length > 0 || (ag.papeis_automaticos || []).length > 0 ? (
                      <>
                        {(ag.usuarios_manuais || []).length > 0 &&
                          `${ag.usuarios_manuais.length} usuário(s): ${ag.usuarios_manuais.slice(0, 3).map(nomeUsuario).join(', ')}${ag.usuarios_manuais.length > 3 ? '...' : ''}`}
                        {(ag.usuarios_manuais || []).length > 0 && (ag.papeis_automaticos || []).length > 0 && ' · '}
                        {(ag.papeis_automaticos || []).length > 0 &&
                          `papéis: ${ag.papeis_automaticos.map(labelPapel).join(', ')}`}
                      </>
                    ) : (
                      'Nenhum destinatário selecionado'
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch
                    data-testid={`agendamento-ativo-switch-${ag.id}`}
                    checked={ag.ativo}
                    onCheckedChange={() => handleToggleAtivo(ag)}
                  />
                  <Button
                    data-testid={`agendamento-testar-btn-${ag.id}`}
                    size="icon"
                    variant="ghost"
                    title="Testar agora (dispara ignorando o horário configurado)"
                    disabled={testandoId === ag.id}
                    onClick={() => handleTestarAgora(ag)}
                  >
                    {testandoId === ag.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                  <Button
                    data-testid={`agendamento-editar-btn-${ag.id}`}
                    size="icon"
                    variant="ghost"
                    onClick={() => abrirEditar(ag)}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    data-testid={`agendamento-excluir-btn-${ag.id}`}
                    size="icon"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => setExcluindoId(ag.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Dialog Criar/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="agendamento-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editandoId ? 'Editar agendamento' : 'Novo agendamento'}</DialogTitle>
            <DialogDescription>Defina quando e para quem este relatório será enviado.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Nome do alerta</Label>
              <Input
                data-testid="agendamento-nome-input"
                value={form.nome_alerta}
                onChange={(e) => setForm({ ...form, nome_alerta: e.target.value })}
                placeholder="Ex: Pendências de Compras (diário)"
              />
            </div>

            <div>
              <Label>Tipo de relatório</Label>
              <Select value={form.tipo_relatorio} onValueChange={(v) => setForm({ ...form, tipo_relatorio: v })}>
                <SelectTrigger data-testid="agendamento-tipo-select" className="mt-1">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {tipos.map((t) => (
                    <SelectItem key={t.valor} value={t.valor}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.tipo_relatorio && (
                <p className="text-xs text-slate-500 mt-1">
                  {tipos.find((t) => t.valor === form.tipo_relatorio)?.descricao}
                  {' '}Texto editável na aba "Regras &amp; Templates" → Templates de Mensagem → <code>{form.tipo_relatorio}</code>.
                </p>
              )}
            </div>

            <div>
              <Label>Dias da semana</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {diasSemana.map((d) => (
                  <button
                    key={d}
                    type="button"
                    data-testid={`agendamento-dia-${d}`}
                    onClick={() => toggleDia(d)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      form.frequencia.includes(d)
                        ? 'bg-amber-500 text-white border-amber-500'
                        : 'bg-white text-slate-600 border-slate-300 hover:border-amber-400'
                    }`}
                  >
                    {DIA_LABEL[d] || d}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Horários de disparo (Brasília)</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  data-testid="agendamento-horario-input"
                  type="time"
                  value={novoHorario}
                  onChange={(e) => setNovoHorario(e.target.value)}
                  className="w-32"
                />
                <Button data-testid="agendamento-add-horario-btn" type="button" size="sm" variant="outline" onClick={adicionarHorario}>
                  <Plus className="w-3 h-3 mr-1" /> Adicionar
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {form.horarios.map((h) => (
                  <Badge key={h} className="bg-amber-100 text-amber-800 gap-1 pr-1">
                    {h}
                    <button data-testid={`agendamento-remover-horario-${h}`} onClick={() => removerHorario(h)} type="button">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
                {form.horarios.length === 0 && <span className="text-xs text-slate-400">Nenhum horário adicionado</span>}
              </div>
            </div>

            <div>
              <Label className="text-base">Matriz de Destinatários</Label>
              <p className="text-xs text-slate-500 mb-2">
                Combine usuários específicos com papéis automáticos — papéis resolvem dinamicamente para quem hoje ocupa aquele papel.
              </p>

              <Label className="text-sm">Usuários específicos</Label>
              <ScrollArea className="h-32 mt-1 border rounded p-2" data-testid="agendamento-destinatarios-lista">
                <div className="space-y-1.5">
                  {usuarios.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer py-0.5" data-testid={`agendamento-destinatario-${u.id}`}>
                      <Checkbox
                        checked={form.usuarios_manuais.includes(u.id)}
                        onCheckedChange={() => toggleDestinatario(u.id)}
                      />
                      <span>{u.full_name || u.email}</span>
                    </label>
                  ))}
                </div>
              </ScrollArea>

              <Label className="text-sm mt-3 block">Papéis automáticos</Label>
              <p className="text-xs text-slate-500 mb-1.5">
                Marque um papel e o sistema envia automaticamente para todos que estiverem marcados com ele em Gestão de Usuários — sem precisar escolher pessoa por pessoa.
              </p>
              <div className="flex flex-wrap gap-3">
                {papeisCatalogo.map((p) => (
                  <label
                    key={p.valor}
                    className="flex items-center gap-2 text-sm cursor-pointer px-3 py-2 rounded border border-slate-200 hover:border-indigo-300"
                    data-testid={`agendamento-papel-checkbox-${p.valor}`}
                  >
                    <Checkbox
                      checked={form.papeis_automaticos.includes(p.valor)}
                      onCheckedChange={() => togglePapel(p.valor)}
                    />
                    {p.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-1 border-t">
              <Label className="text-base">Agendamento ativo</Label>
              <Switch
                data-testid="agendamento-ativo-form-switch"
                checked={form.ativo}
                onCheckedChange={(v) => setForm({ ...form, ativo: v })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button data-testid="agendamento-salvar-btn" onClick={handleSalvar} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação de exclusão */}
      <AlertDialog open={!!excluindoId} onOpenChange={(open) => !open && setExcluindoId(null)}>
        <AlertDialogContent data-testid="agendamento-excluir-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir agendamento?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="agendamento-excluir-confirmar-btn" onClick={handleExcluir} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
